import { API_ENDPOINTS } from '@/api/endpoints';
import {
  ApiLineItem,
  useGetCostEstimate,
  useUpdateCostEstimate,
} from '@/api/services/costEstimateService';
import { Notification } from '@/components/Notification';
import { BaseDecarbFooterBar } from '@/decarbonization/components/BaseDecarbFooterBar';
import { usePencilsDown } from '@/decarbonization/hooks/usePencilsDown.ts';
import { useNotification } from '@/hooks/useNotification';
import { useOrg } from '@/organization/useOrg';
import { Project } from '@/stores/ProjectStore';
import { Subproject } from '@/stores/SubprojectStore';
import { apiFetch } from '@/util/apiClient';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Routes } from '../../CSIRouter';
import { CapitalCostTable } from './CapitalCostTable';
import { CostSummarySidebar } from './CostSummarySidebar';
import { OperationCostTable } from './OperationCostTable';
import {
  CAPITAL_CATEGORIES,
  LineItemState,
  OPERATION_CATEGORIES,
  initLineItems,
  validateLocalCostMultiplier,
} from './types';

const FONT_HEADER = 'Montserrat, sans-serif';

// Categories that must be filled before FINISH is enabled
// todo: add requirements back once reports need it for calculations
// 'planning_design',
// 'contingency',
// 'construction',
// 'personnel',
// 'minor_maintenance',
// 'water_utility',
// 'energy_utility',
const REQUIRED_CATEGORIES = new Set<string>([]);

function isFormValid(
  capitalItems: LineItemState[],
  operationItems: LineItemState[],
): boolean {
  return [...capitalItems, ...operationItems]
    .filter((item) => REQUIRED_CATEGORIES.has(item.category))
    .every(
      (item) =>
        item.ownerAmountFiscalyear !== '' &&
        item.contractorAmountFiscalyear !== '',
    );
}

function apiItemToLineItemState(
  apiItem: ApiLineItem,
  section: 'capital' | 'operation',
): LineItemState {
  return {
    clientId: crypto.randomUUID(),
    id: apiItem.id,
    section,
    category: apiItem.category,
    label: apiItem.label ?? '',
    ownerAmountFiscalyear: apiItem.ownerAmountFiscalyear ?? '',
    contractorAmountFiscalyear: apiItem.contractorAmountFiscalyear ?? '',
    ownerAmountTotal: apiItem.ownerAmountTotal ?? '',
    contractorAmountTotal: apiItem.contractorAmountTotal ?? '',
    sortOrder: apiItem.sortOrder,
    subprojectBreakdowns: (apiItem.subprojectBreakdowns ?? []).map((bd) => ({
      subprojectId: bd.subproject,
      ownerAmountFiscalyear: bd.ownerAmountFiscalyear ?? '',
      contractorAmountFiscalyear: bd.contractorAmountFiscalyear ?? '',
      ownerAmountTotal: bd.ownerAmountTotal ?? '',
      contractorAmountTotal: bd.contractorAmountTotal ?? '',
    })),
  };
}

function initFromApiItems(
  apiItems: ApiLineItem[],
  section: 'capital' | 'operation',
  categories: typeof CAPITAL_CATEGORIES,
): LineItemState[] {
  const fixed = categories.map((cat) => {
    const apiItem = apiItems.find(
      (li) => li.section === section && li.category === cat.category,
    );
    return apiItem
      ? apiItemToLineItemState(apiItem, section)
      : {
          clientId: crypto.randomUUID(),
          section,
          category: cat.category,
          label: '',
          ownerAmountFiscalyear: '',
          contractorAmountFiscalyear: '',
          ownerAmountTotal: '',
          contractorAmountTotal: '',
          sortOrder: cat.sortOrder,
          subprojectBreakdowns: [],
        };
  });
  const custom = apiItems
    .filter((li) => li.section === section && li.category === 'custom')
    .map((li) => apiItemToLineItemState(li, section));
  return [...fixed, ...custom];
}

function buildSavePayload(
  capitalItems: LineItemState[],
  operationItems: LineItemState[],
  dataSource: string,
  referenceYear: string,
  rateInflationOwner: string,
  rateInflationLand: string,
  rateInflationCostEscalation: string,
  localCostMultiplier: string,
  costBasis: string,
) {
  const toApiItems = (items: LineItemState[]) =>
    items
      .filter(
        (item) =>
          item.category !== 'custom' ||
          item.label !== '' ||
          item.ownerAmountFiscalyear !== '' ||
          item.contractorAmountFiscalyear !== '' ||
          item.ownerAmountTotal !== '' ||
          item.contractorAmountTotal !== '',
      )
      .map((item) => ({
        section: item.section,
        category: item.category,
        label: item.label,
        ownerAmountFiscalyear:
          item.ownerAmountFiscalyear !== '' ? item.ownerAmountFiscalyear : null,
        contractorAmountFiscalyear:
          item.contractorAmountFiscalyear !== ''
            ? item.contractorAmountFiscalyear
            : null,
        ownerAmountTotal:
          item.ownerAmountTotal !== '' ? item.ownerAmountTotal : null,
        contractorAmountTotal:
          item.contractorAmountTotal !== '' ? item.contractorAmountTotal : null,
        sortOrder: item.sortOrder,
        subprojectBreakdowns: item.subprojectBreakdowns.map((bd) => ({
          subproject: bd.subprojectId,
          ownerAmountFiscalyear:
            bd.ownerAmountFiscalyear !== '' ? bd.ownerAmountFiscalyear : null,
          contractorAmountFiscalyear:
            bd.contractorAmountFiscalyear !== ''
              ? bd.contractorAmountFiscalyear
              : null,
          ownerAmountTotal:
            bd.ownerAmountTotal !== '' ? bd.ownerAmountTotal : null,
          contractorAmountTotal:
            bd.contractorAmountTotal !== '' ? bd.contractorAmountTotal : null,
        })),
      }));

  return {
    dataSource: dataSource || null,
    referenceYear: referenceYear ? parseInt(referenceYear) : null,
    rateInflationOwner: rateInflationOwner || null,
    rateInflationLand: rateInflationLand || null,
    rateInflationCostEscalation: rateInflationCostEscalation || null,
    localCostMultiplier: localCostMultiplier || null,
    costBasis: costBasis || null,
    lineItems: [...toApiItems(capitalItems), ...toApiItems(operationItems)],
  };
}

const AMOUNT_FIELDS: (keyof LineItemState)[] = [
  'ownerAmountFiscalyear',
  'contractorAmountFiscalyear',
  'ownerAmountTotal',
  'contractorAmountTotal',
];

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries([
  ...CAPITAL_CATEGORIES.map((c) => [c.category, c.label]),
  ...OPERATION_CATEGORIES.map((c) => [c.category, c.label]),
]);

function validateBeforeSave(
  capitalItems: LineItemState[],
  operationItems: LineItemState[],
  rateInflationOwner: string,
  rateInflationLand: string,
  rateInflationCostEscalation: string,
  localCostMultiplier: string,
): string[] {
  const errors: string[] = [];

  const scalars: [string, string][] = [
    [rateInflationOwner, 'Inflation Rate (Owner)'],
    [rateInflationLand, 'Inflation Rate (Land)'],
    [rateInflationCostEscalation, 'Inflation Rate (Cost Escalation)'],
  ];
  for (const [value, label] of scalars) {
    if (value !== '' && isNaN(parseFloat(value))) {
      errors.push(`"${label}" must be a number.`);
    }
  }

  const multiplierError = validateLocalCostMultiplier(localCostMultiplier);
  if (multiplierError) errors.push(multiplierError);

  for (const item of [...capitalItems, ...operationItems]) {
    const rowLabel =
      item.category === 'custom'
        ? item.label || 'Custom row'
        : (CATEGORY_LABELS[item.category] ?? item.category);
    for (const field of AMOUNT_FIELDS) {
      const value = item[field] as string;
      if (value !== '' && isNaN(parseFloat(value))) {
        errors.push(
          `"${rowLabel}" — amount must be a number (got "${value}").`,
        );
      }
    }
  }

  return errors;
}

// ------------------------------------------------------------
// Cost Estimate Validation bar
// ------------------------------------------------------------

const validationFieldSx = {
  flex: 1,
  '& input': { color: 'primary.main' },
  '& .MuiInputLabel-root': { color: 'rgba(14, 70, 63, 0.7)' },
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { fontSize: '0.78rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0E463F' },
  '& .MuiInputAdornment-root': { color: 'primary.main' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(14, 70, 63, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(14, 70, 63, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#0E463F', borderWidth: 2 },
  },
} as const;

interface CostEstimateValidationBarProps {
  rateInflationOwner: string;
  rateInflationLand: string;
  rateInflationCostEscalation: string;
  onRateInflationOwnerChange: (v: string) => void;
  onRateInflationLandChange: (v: string) => void;
  onRateInflationCostEscalationChange: (v: string) => void;
}

function CostEstimateValidationBar({
  rateInflationOwner,
  rateInflationLand,
  rateInflationCostEscalation,
  onRateInflationOwnerChange,
  onRateInflationLandChange,
  onRateInflationCostEscalationChange,
}: CostEstimateValidationBarProps) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          pl: 6,
          pr: 1,
          pt: 1.25,
          pb: 2,
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Typography
          variant='caption'
          sx={{
            fontFamily: FONT_HEADER,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: 'text.primary',
          }}
        >
          Cost Estimate Inflation Factors
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          pt: 2.5,
          pb: 1.5,
          pl: 6,
          pr: 12,
          bgcolor: '#D5E4D5',
          border: '1px solid',
          borderColor: 'rgba(14, 70, 63, 0.2)',
          borderTop: 0,
        }}
      >
        <TextField
          size='small'
          label="Rate of Inflation for Owner's Expense"
          value={rateInflationOwner}
          onChange={(e) => onRateInflationOwnerChange(e.target.value)}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position='end'>%</InputAdornment>,
            },
          }}
          sx={validationFieldSx}
        />
        <TextField
          size='small'
          label='Rate of Inflation for Land Values'
          value={rateInflationLand}
          onChange={(e) => onRateInflationLandChange(e.target.value)}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position='end'>%</InputAdornment>,
            },
          }}
          sx={validationFieldSx}
        />
        <TextField
          size='small'
          label='Rate of Inflation for Cost Escalation'
          value={rateInflationCostEscalation}
          onChange={(e) => onRateInflationCostEscalationChange(e.target.value)}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position='end'>%</InputAdornment>,
            },
          }}
          sx={validationFieldSx}
        />
      </Box>
    </Box>
  );
}

// ------------------------------------------------------------
// Header
// ------------------------------------------------------------

interface CostEstimatesHeaderProps {
  projectName: string;
}

function CostEstimatesHeader({ projectName }: CostEstimatesHeaderProps) {
  return (
    <Box
      sx={{
        mt: '35px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        pointerEvents: 'auto',
        ml: '70px',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pb: '15px' }}
      >
        <Typography
          variant='h4'
          component='h1'
          sx={{
            fontFamily: 'Futura PT, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(24px, 1.85vw, 35px)',
            color: 'rgba(40, 40, 40, 1)',
            textShadow: {
              xs: '-2px 2px rgba(101, 166, 178, 1)',
              lg: '-4px 3px rgba(101, 166, 178, 1)',
            },
            textTransform: 'uppercase',
            letterSpacing: 'clamp(8px, 0.75vw, 14px)',
            lineHeight: 1,
            mb: 'clamp(4px, 0.4vw, 8px)',
          }}
        >
          PROJECT CREATION
        </Typography>
        <Typography
          variant='h5'
          component='h2'
          sx={{
            fontFamily: 'Futura PT, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(45px, 3.5vw, 68px)',
            color: 'rgba(40, 40, 40, 1)',
            textShadow: {
              xs: '-2px 2px rgba(101, 166, 178, 1)',
              lg: '-4px 3px rgba(101, 166, 178, 1)',
            },
            textTransform: 'uppercase',
            letterSpacing: 'clamp(8px, 0.75vw, 14px)',
            lineHeight: 1,
          }}
        >
          {projectName}
        </Typography>
      </Box>
    </Box>
  );
}

// ------------------------------------------------------------
// Main screen
// ------------------------------------------------------------

export function CostEstimates() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const pencilsDown = usePencilsDown();
  const { selectedOrg } = useOrg();
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();

  const [capitalItems, setCapitalItems] = useState<LineItemState[]>(() =>
    initLineItems('capital'),
  );
  const [operationItems, setOperationItems] = useState<LineItemState[]>(() =>
    initLineItems('operation'),
  );
  const [dataSource, setDataSource] = useState('');
  const [referenceYear, setReferenceYear] = useState('');
  const [rateInflationOwner, setRateInflationOwner] = useState('');
  const [rateInflationLand, setRateInflationLand] = useState('');
  const [rateInflationCostEscalation, setRateInflationCostEscalation] =
    useState('');
  const [localCostMultiplier, setLocalCostMultiplier] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: project, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', selectedOrg?.id, projectId],
    queryFn: async () => {
      if (!selectedOrg?.id || !projectId) return null;
      return apiFetch<Project>(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.DETAIL(selectedOrg.id, projectId),
      );
    },
    enabled: !!selectedOrg?.id && !!projectId,
  });

  const { data: subprojects = [], isLoading: isSubprojectsLoading } = useQuery({
    queryKey: ['subprojects', selectedOrg?.id, projectId],
    queryFn: async () => {
      if (!selectedOrg?.id || !projectId) return [];
      return apiFetch<Subproject[]>(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.SUBPROJECTS.LIST(
          selectedOrg.id,
          projectId,
        ),
      );
    },
    enabled: !!selectedOrg?.id && !!projectId,
  });

  const { data: costEstimateData, isLoading: isCostEstimateLoading } =
    useGetCostEstimate(selectedOrg?.id, projectId);

  const updateMutation = useUpdateCostEstimate(selectedOrg?.id, projectId);

  // Initialize form state from API on first load
  useEffect(() => {
    if (costEstimateData && !isInitialized) {
      const apiItems = costEstimateData.lineItems ?? [];
      const capitalFromApi = apiItems.filter((li) => li.section === 'capital');
      const operationFromApi = apiItems.filter(
        (li) => li.section === 'operation',
      );

      if (capitalFromApi.length > 0 || operationFromApi.length > 0) {
        setCapitalItems(
          initFromApiItems(capitalFromApi, 'capital', CAPITAL_CATEGORIES),
        );
        setOperationItems(
          initFromApiItems(operationFromApi, 'operation', OPERATION_CATEGORIES),
        );
      }

      setDataSource(costEstimateData.dataSource ?? '');
      setReferenceYear(costEstimateData.referenceYear?.toString() ?? '');
      setRateInflationOwner(costEstimateData.rateInflationOwner ?? '');
      setRateInflationLand(costEstimateData.rateInflationLand ?? '');
      setRateInflationCostEscalation(
        costEstimateData.rateInflationCostEscalation ?? '',
      );
      setLocalCostMultiplier(costEstimateData.localCostMultiplier ?? '');
      setCostBasis(costEstimateData.costBasis ?? '');
      setIsInitialized(true);
    }
  }, [costEstimateData, isInitialized]);

  const finishIsDisabled = !isFormValid(capitalItems, operationItems);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSaveReturn = async () => {
    const validationErrors = validateBeforeSave(
      capitalItems,
      operationItems,
      rateInflationOwner,
      rateInflationLand,
      rateInflationCostEscalation,
      localCostMultiplier,
    );
    if (validationErrors.length > 0) {
      showError(validationErrors.join('\n'));
      return;
    }
    const payload = buildSavePayload(
      capitalItems,
      operationItems,
      dataSource,
      referenceYear,
      rateInflationOwner,
      rateInflationLand,
      rateInflationCostEscalation,
      localCostMultiplier,
      costBasis,
    );
    try {
      await updateMutation.mutateAsync(payload);
      showSuccess('Cost estimate saved.');
      navigate(
        `/${Routes.Project.path.replace(':projectId', projectId ?? '')}/${Routes.Project.ProjectDetails.path}`,
      );
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : 'Failed to save cost estimate. Please try again.',
      );
    }
  };

  const handleFinish = async () => {
    const validationErrors = validateBeforeSave(
      capitalItems,
      operationItems,
      rateInflationOwner,
      rateInflationLand,
      rateInflationCostEscalation,
      localCostMultiplier,
    );
    if (validationErrors.length > 0) {
      showError(validationErrors.join('\n'));
      return;
    }
    const payload = buildSavePayload(
      capitalItems,
      operationItems,
      dataSource,
      referenceYear,
      rateInflationOwner,
      rateInflationLand,
      rateInflationCostEscalation,
      localCostMultiplier,
      costBasis,
    );
    try {
      await updateMutation.mutateAsync(payload);
      showSuccess('Cost estimate saved.');
      navigate(
        `/${Routes.Project.path.replace(':projectId', projectId ?? '')}/${Routes.Project.ProjectDetails.path}`,
      );
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : 'Failed to save cost estimate. Please try again.',
      );
    }
  };

  if (isCostEstimateLoading || isProjectLoading || isSubprojectsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        overflow: 'hidden',
      }}
    >
      <CostEstimatesHeader projectName={project?.name ?? ''} />

      <Box
        sx={{
          flex: 1,
          px: { xs: 2, md: 4 },
          pb: 3,
          display: 'flex',
          flexDirection: 'column !important',
          minHeight: 0,
          maxHeight: '85vh',
        }}
      >
        <Grid
          container
          spacing={3}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            minHeight: 0,
            bgcolor: 'background.card',
          }}
        >
          {/* Main content panel (~75%) */}
          <Grid size={{ xs: 12, md: 9 }}>
            <Stack spacing={4}>
              {/* Cost Estimate Validation bar */}
              <CostEstimateValidationBar
                rateInflationOwner={rateInflationOwner}
                rateInflationLand={rateInflationLand}
                rateInflationCostEscalation={rateInflationCostEscalation}
                onRateInflationOwnerChange={setRateInflationOwner}
                onRateInflationLandChange={setRateInflationLand}
                onRateInflationCostEscalationChange={
                  setRateInflationCostEscalation
                }
              />

              {/* Capital Cost Estimation section */}
              <CapitalCostTable
                lineItems={capitalItems}
                subprojects={subprojects}
                fyName={project?.fiscalYearDetails?.name}
                onChange={setCapitalItems}
              />

              {/* Annual Operation Cost Estimation section */}
              <OperationCostTable
                lineItems={operationItems}
                subprojects={subprojects}
                onChange={setOperationItems}
              />
            </Stack>
          </Grid>

          {/* Sidebar (~25%) */}
          <Grid
            size={{ xs: 12, md: 3 }}
            sx={{ position: 'sticky', top: 0, alignSelf: 'flex-start' }}
          >
            <CostSummarySidebar
              capitalItems={capitalItems}
              operationItems={operationItems}
              project={project}
              dataSource={dataSource}
              referenceYear={referenceYear}
              rateInflationOwner={rateInflationOwner}
              rateInflationLand={rateInflationLand}
              rateInflationCostEscalation={rateInflationCostEscalation}
              localCostMultiplier={localCostMultiplier}
              regionalMultiplier={costEstimateData?.regionalMultiplier ?? null}
              costBasis={costBasis}
              onDataSourceChange={setDataSource}
              onReferenceYearChange={setReferenceYear}
              onLocalCostMultiplierChange={setLocalCostMultiplier}
              onCostBasisChange={setCostBasis}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ flexShrink: 0, width: '100%' }}>
        <BaseDecarbFooterBar
          backText={'Cancel'}
          onBackClick={handleBack}
          onNextClick={handleFinish}
          nextIsDisabled={finishIsDisabled}
          nextIsDisabledText={'INCOMPLETE DATA'}
          onReturnToHubClick={handleSaveReturn}
          returnText={
            pencilsDown ? 'RETURN TO SUMMARY' : 'SAVE & EXIT TO SUMMARY'
          }
        />
      </Box>

      <Notification notification={notification} onClose={hideNotification} />
    </Box>
  );
}
